/**
 * Spanish strings for the MFA verification component.
 *
 * @package @puffin-core/mfa-ui
 * @module @puffin-core/mfa-ui/strings/es
 *
 * ## Why this file did not exist until 2026-08-18
 *
 * `MfaVerification` resolves through `useMfaTranslator(externalT)`, whose documented fallback is
 * the shipped ENGLISH `DEFAULT_MFA_STRINGS`. The `t` prop was optional, and a census of the four
 * render sites found it passed at **one** of them (`login-shell/LoginForm.jsx`). So the MFA
 * challenge — *"Verify Your Identity" · "Enter the 6-digit code from your authenticator app" ·
 * "Verify" · "Backup Code"* — rendered English in every locale on the other three, which are the
 * pos-web challenge provider, the port-web setup page, and re-auth's own password step.
 *
 * `web/no-hardcoded-strings` scores this **0** in every app and always will: the literals live in a
 * package and the gate censuses `apps/**`. RECIPE miss-class 9 — the same shape as the `auth-shell`
 * defect that shipped "Skip to main content" in English across five apps.
 *
 * ## Register: USTED (CATCONV-001)
 *
 * Ecosystem-wide for authenticated surfaces. This is a security prompt shown to an employee by
 * their employer's software; the formal register is the correct one and matches every sibling
 * bundle.
 *
 * ## Placeholders
 *
 * `{{maskedPhone}}` / `{{maskedEmail}}` / `{{seconds}}` are DOUBLE-curly, matching `en.js` and the
 * package's own `interpolate()`. Do not "normalise" them to single-curly to match `re-auth` —
 * `useMfaTranslate` expands whatever the bundle uses, and the two packages differ deliberately.
 *
 * ⚠️ No key here is plural-shaped (no final `_`-segment is a CLDR category), so no `_many` form is
 * required. If a `{{count}}` stem is ever added it needs `_one`/`_other` in EN and
 * `_one`/`_many`/`_other` in ES — Spanish carries a `many` category English does not, and omitting
 * it renders the SINGULAR for a large count.
 */

export const MFA_STRINGS_ES = Object.freeze({
  title: 'Verifique su identidad',
  subtitle_totp: 'Introduzca el código de 6 dígitos de su aplicación de autenticación.',
  subtitle_sms: 'Introduzca el código enviado a {{maskedPhone}}.',
  subtitle_email: 'Introduzca el código enviado a {{maskedEmail}}.',
  subtitle_send_sms: 'Enviaremos un código a {{maskedPhone}}.',
  subtitle_send_email: 'Enviaremos un código a {{maskedEmail}}.',
  subtitle_passkey: 'Use la clave de acceso guardada en este dispositivo.',
  label_code: 'Código de verificación',
  label_backup: 'Código de respaldo',
  btn_verify: 'Verificar',
  btn_use_passkey: 'Usar clave de acceso',
  btn_verifying: 'Verificando...',
  btn_back: 'Atrás',
  btn_send: 'Enviar código',
  btn_sending: 'Enviando...',
  btn_resend: 'Reenviar código',
  resend_countdown: '({{seconds}} s)',
  link_try_another: 'Probar otro método',
  link_backup: 'Usar un código de respaldo',
  link_return_to_signin: 'Volver al inicio de sesión',
  title_backup: 'Usar un código de respaldo',
  subtitle_backup: 'Introduzca uno de sus códigos de respaldo de 8 caracteres.',
  title_methods: 'Elija un método de verificación.',
  method_totp: 'Aplicación de autenticación',
  method_totp_hint: 'Introduzca el código de su aplicación',
  method_sms: 'Mensaje de texto',
  method_email: 'Correo electrónico',
  method_passkey: 'Clave de acceso',
  method_passkey_hint: 'Use el bloqueo de pantalla de su dispositivo',

  // F-PASSKEY-HOST (2026-10-24): ningún método que nombra el desafío se puede usar aquí.
  notice_no_usable_method: 'Ninguno de sus métodos de verificación se puede usar aquí.',

  status_title: 'Autenticación multifactor',
  status_enabled: 'Activada',
  status_disabled: 'Desactivada',
  status_desc_enabled:
    'La autenticación multifactor está activada mediante {{methods}}. Se le pedirá un código de verificación al iniciar sesión.',
  status_desc_disabled:
    'Añada una capa adicional de seguridad a su cuenta activando la autenticación multifactor.',
  status_btn_enable: 'Activar la autenticación multifactor',
  backup_btn_regenerate: 'Regenerar los códigos de respaldo',
  backup_btn_regenerating: 'Regenerando...',
  setup_btn_verify: 'Verificar',
  setup_btn_verifying: 'Verificando...',
  backup_title: 'Códigos de respaldo',
  setup_title_configure: 'Configure la aplicación de autenticación',
  setup_manual_key: 'Clave de entrada manual',
  setup_title_verify: 'Verifique la configuración',
  setup_title_backup: 'Guarde los códigos de respaldo',
  setup_region_label: 'Configuración de la autenticación multifactor',
  setup_step_configure: 'Configurar',
  setup_step_verify: 'Verificar',
  setup_step_backup: 'Códigos de respaldo',
  setup_configure_intro:
    'Escanee el código QR con su aplicación de autenticación para agregar {{brand}}, o ingrese la clave a continuación manualmente.',
  setup_qr_label: 'Código QR que agrega {{brand}} a una aplicación de autenticación',
  setup_uri_label:
    'Enlace de configuración (abre su aplicación de autenticación en este dispositivo)',
  setup_qr_loading: 'Generando el código QR…',
  setup_qr_unavailable:
    'No se pudo generar el código QR. Ingrese la clave a continuación manualmente o abra el enlace de configuración.',
  setup_btn_cancel: 'Cancelar',
  setup_btn_next: 'Siguiente',
  enroll_title: 'Configure la verificación en dos pasos',
  enroll_intro:
    'Su cuenta de {{appName}} necesita un segundo paso de inicio de sesión antes de continuar. Confirme su contraseña para empezar.',
  enroll_password_label: 'Contraseña',
  enroll_btn_continue: 'Continuar',
  enroll_btn_cancel: 'Cancelar',
  enroll_already_title: 'La verificación en dos pasos ya está configurada',
  enroll_already_body:
    'La verificación en dos pasos ya está configurada para esta cuenta. Inicie sesión para continuar.',
  enroll_already_backup_codes:
    'Si no guardó sus códigos de respaldo, inicie sesión y use Regenerar los códigos de respaldo en su configuración de seguridad. Al regenerarlos, se reemplazan los códigos de esta configuración.',
  enroll_already_btn_sign_in: 'Iniciar sesión',
  // SECREQ S6b: un inicio de sesión retenido hasta crear una clave de acceso (`<PasskeyEnrollment>`).
  enroll_passkey_title: 'Configure una clave de acceso',
  enroll_passkey_intro:
    'Su empresa exige una clave de acceso para su cuenta de {{appName}}. Use el bloqueo de pantalla de su dispositivo para crear una y luego continúe iniciando sesión.',
  enroll_passkey_btn_create: 'Crear clave de acceso',
  enroll_passkey_already_title: 'Ya hay una clave de acceso configurada',
  enroll_passkey_already_body:
    'Esta cuenta ya tiene una clave de acceso. Inicie sesión con ella para continuar.',
  // N152 (MFA-SETUP-CONVERGE): los pasos de verificación y de códigos del asistente, `<BackupCodesCard>` y la
  // configuración dentro de la sesión (`<MfaSettingsCard>`).
  setup_verify_intro:
    'Ingrese el código de 6 dígitos de su aplicación de autenticación para confirmar la configuración.',
  setup_code_label: 'Código de verificación',
  setup_backup_intro:
    'Guarde estos códigos de respaldo en un lugar seguro. Cada código solo se puede usar una vez.',
  setup_backup_ack: 'Guardé estos códigos de respaldo',
  setup_btn_complete: 'Completar la configuración',
  backup_desc:
    'Los códigos de respaldo le permiten entrar a su cuenta si pierde su dispositivo de autenticación. Cada código solo se puede usar una vez.',
  backup_remaining: '{{count}} restantes',
  backup_regen_label:
    'Ingrese un código de su aplicación de autenticación para regenerar sus códigos de respaldo',
  backup_regen_success: 'Sus códigos de respaldo se regeneraron. Guarde los códigos nuevos ahora.',
  backup_btn_copy: 'Copiar códigos',
  backup_btn_download: 'Descargar códigos',
  backup_btn_print: 'Imprimir códigos',
  backup_copied: 'Códigos copiados',
  backup_copy_failed: 'No se pudieron copiar los códigos. Selecciónelos y cópielos usted mismo.',
  backup_file_heading: 'Códigos de respaldo',
  backup_file_intro:
    'Guarde estos códigos en un lugar seguro. Cada código solo se puede usar una vez.',
  backup_file_generated: 'Generados: {{date}}',
  backup_confirm_title: '¿Regenerar sus códigos de respaldo?',
  backup_confirm_body:
    'Los códigos de respaldo que le quedan dejarán de funcionar. Guarde los códigos nuevos en cuanto aparezcan.',
  backup_confirm_btn: 'Regenerar códigos',
  backup_confirm_cancel: 'Conservar mis códigos',
  status_btn_disable: 'Desactivar la autenticación multifactor',
  enroll_intro_settings:
    'Active la verificación en dos pasos para su cuenta de {{appName}}. Confirme su contraseña para empezar.',
  settings_disable_title: 'Desactive la verificación en dos pasos',
  settings_disable_intro:
    'Ingrese un código de su aplicación de autenticación, o un código de respaldo, para desactivar la verificación en dos pasos.',
  error_status_failed:
    'No se pudo cargar el estado de su verificación en dos pasos. Inténtelo de nuevo.',
  error_setup_failed: 'No se pudo configurar la verificación en dos pasos. Inténtelo de nuevo.',
  error_disable_failed: 'No se pudo desactivar la verificación en dos pasos. Inténtelo de nuevo.',
  error_regen_failed: 'No se pudieron regenerar sus códigos de respaldo. Inténtelo de nuevo.',

  // ───────────────────────────────────────────────────────────────────────────
  // `<PasskeyManagement>` — folded in from `i18n/passkeyManagement.es.js` by I18N-RENDER-002
  // (2026-08-20). Values carried over verbatim; only the placeholder braces changed, from `{count}`
  // / `{name}` to `{{count}}` / `{{name}}`, so they match this namespace's single interpolator
  // (`Q-BRACE-1`).
  //
  // ⚠️ PROVENANCE UNCHANGED — every leaf here remains **machine-translated and unreviewed**, exactly
  // as the retired `passkeyManagement._meta.json` recorded (`es._status: "machine"`) and as
  // `CRITICAL-FIXES/aas-i18n-002/ES-REVIEW-MANIFEST.md` already treats every leaf of this file. The
  // fold moves 26 machine leaves into a 25-leaf machine catalog, so no leaf changes status; the
  // manifest's per-catalog count for this file needs updating 25 → 51.
  //
  // The retired sidecar's reviewer notes, preserved because they are instructions to a human:
  //   · `passkey_registeredCount` / `passkey_daysAgo` / `passkey_weeksAgo` must keep the literal
  //     `{{count}}` token, and `passkey_removeAriaLabel` / `passkey_removeDialogBody` the literal
  //     `{{name}}` token — Spanish word order may place them differently than English.
  //   · "passkey" is deliberately left as the English term in the ES copy where it appears, matching
  //     Apple's and Google's Spanish UIs.
  passkey_title: 'Claves de acceso',
  passkey_registeredCount: '{{count}} registradas',
  // 🔴 REGISTER CORRECTED in the fold, `te`/`tu` → `le`/`su`. The ecosystem addresses users as
  // **usted**, and this leaf shipped in `tú`. It was invisible because
  // `createCatalogConventionTest` is applied to `strings/` bundles and the prop-injected
  // `i18n/*.es.js` layout was never register-checked — so moving the catalog into the checked layout
  // is what found it. Same class as the five `tú` leaves found in a shared coach-mark bundle
  // "long after the register sweep was called complete".
  passkey_description:
    'Las claves de acceso le permiten iniciar sesión con su huella digital o reconocimiento facial: más rápido y más seguro que las contraseñas.',
  passkey_columnName: 'Nombre',
  passkey_columnCreated: 'Creada',
  passkey_columnLastUsed: 'Último uso',
  passkey_columnActions: 'Acciones',
  passkey_unnamedPasskey: 'Clave de acceso sin nombre',
  passkey_removeAction: 'Eliminar',
  passkey_removeAriaLabel: 'Eliminar la clave de acceso {{name}}',
  passkey_never: 'Nunca',
  passkey_today: 'Hoy',
  passkey_yesterday: 'Ayer',
  passkey_daysAgo: 'hace {{count}} días',
  passkey_weeksAgo: 'hace {{count}} semanas',
  passkey_deviceNameLabel: 'Nombre del dispositivo (opcional)',
  passkey_deviceNamePlaceholder: 'p. ej. Portátil de trabajo, Teléfono',
  passkey_registerAction: 'Registrar clave de acceso',
  passkey_registeringAction: 'Registrando...',
  passkey_cancelAction: 'Cancelar',
  passkey_removeDialogTitle: 'Eliminar clave de acceso',
  // 🔴 REGISTER CORRECTED, `quieres`/`podrás` → `quiere`/`podrá` (see `passkey_description`).
  passkey_removeDialogBody:
    '¿Seguro que quiere eliminar "{{name}}"? Ya no podrá iniciar sesión con esta clave de acceso.',
  passkey_removingAction: 'Eliminando...',
  // 🔴 REGISTER CORRECTED, `Registra` → `Registre`. The convention matcher did NOT flag this one —
  // it scores pronouns and conjugated verbs, and a bare `tú` IMPERATIVE (`Registra`) is shaped like
  // the `usted` third person of a different verb. Found by reading the leaves the matcher named and
  // then re-reading their neighbours, which is the habit the matcher cannot replace.
  passkey_emptyNotice:
    'Aún no hay claves de acceso registradas. Registre una para habilitar el inicio de sesión sin contraseña.',
  passkey_registerNewAction: 'Registrar nueva clave de acceso',
  passkey_retryAction: 'Reintentar',
  // ECOSYSTEM-EMAIL-001 Q-EE-21 unit 1: the passkey card's failures (machine-translated, awaiting review).
  passkey_errorLoad: 'No se pudieron cargar las claves de acceso. Revise su conexión.',
  passkey_errorRegister: 'No se pudo registrar la clave de acceso.',
  passkey_errorRegisterExpired: 'La configuración de la clave de acceso caducó. Vuelva a empezar.',
  passkey_errorCancelled: 'Se canceló la creación de la clave de acceso',
  passkey_errorAlreadyRegistered: 'Este dispositivo ya tiene una clave de acceso para su cuenta.',
  passkey_errorRemove: 'No se pudo eliminar la clave de acceso. Inténtelo de nuevo.',
  passkey_errorRemoveGone: 'Esa clave de acceso ya se había eliminado.',
  passkey_stepUpOperation: 'agregar una clave de acceso',
  passkey_errorStepUpCancelled: 'Confirme su identidad para agregar una clave de acceso.',
  passkey_stepUpRemoveOperation: 'quitar una clave de acceso',
  passkey_errorStepUpRemoveCancelled: 'Confirme su identidad para quitar una clave de acceso.',
  passkey_errorLastFactor:
    'Esta clave de acceso es su único segundo factor. Primero desactive la verificación en dos pasos o agregue otro método.',
  passkey_errorEmailAlone:
    'Quitar esta clave de acceso dejaría un código por correo electrónico como su único segundo factor. Primero agregue otro método.',
  passkey_errorMethodsChanged: 'Sus métodos de segundo factor cambiaron. Vuelva a intentarlo.',
  passkey_backupCodesIntro:
    'Esta clave de acceso activó la verificación en dos pasos. Guarde estos códigos de respaldo: cada uno le permite iniciar sesión una vez si no puede usar su clave de acceso.',
  // VOLUME-PRICING-001 MFA-STEPUP-FLEET: the in-session step-up dialog (`createMfaStepUp`).
  stepUp_title: 'Se requiere verificación MFA',
  stepUp_action_generic: 'Esta acción',
  stepUp_requires_verification: '{{action}} requiere verificación adicional',
  stepUp_close: 'Cerrar ventana',
  stepUp_cancel: 'Cancelar',
  stepUp_error_no_token: 'La verificación adicional respondió sin un token de sesión',
  stepUp_error_failed: 'La verificación MFA falló',
  stepUp_error_session_ended:
    'Su sesión ha terminado. Inicie sesión de nuevo y vuelva a intentarlo.',
  stepUp_error_passkey_cancelled:
    'Se cerró la solicitud de la clave de acceso. Vuelva a intentarlo o introduzca un código.',
  stepUp_enrollment_title: 'Configure la verificación en dos pasos',
  stepUp_enrollment_required:
    '{{action}} requiere la verificación en dos pasos, y todavía no la ha configurado. Configúrela y vuelva a intentarlo.',
  stepUp_enrollment_setup: 'Configurar la verificación en dos pasos',
  stepUp_error_method_not_enrolled:
    'Ese método no está configurado para su cuenta. Elija otro o configure uno en su configuración de seguridad.',
  // F-PASSKEY-HOST (2026-10-24).
  stepUp_methods_loading: 'Comprobando cómo puede verificar su identidad…',
  error_rate_limited: 'Demasiados intentos. Espere unos minutos y vuelva a intentarlo.',
  error_network:
    'No pudimos comunicarnos con el servidor. Revise su conexión y vuelva a intentarlo.',
  error_enrollment_expired:
    'Se agotó el tiempo de configuración. Vuelva al inicio de sesión y empiece de nuevo.',
  error_password_incorrect: 'Esa contraseña no es correcta. Inténtelo de nuevo.',
  error_code_incorrect:
    'Ese código no es correcto. Revise su aplicación de autenticación e inténtelo de nuevo.',
  error_change_not_recorded:
    'Su cambio no se realizó porque no se pudo registrar. Inténtelo de nuevo en un momento.',
  error_passkey_unavailable:
    'Las claves de acceso no están disponibles por un momento. Inténtelo de nuevo.',
  error_already_enabled: 'La verificación en dos pasos ya está configurada para esta cuenta.',
  error_setup_expired:
    'Esta configuración ya no está pendiente. Vuelva a empezar a configurar la verificación en dos pasos.',
  error_not_enabled: 'La verificación en dos pasos no está configurada para esta cuenta.',
  error_sms_opted_out:
    'Su número se dio de baja de los mensajes de {{number}}. Envíe START a {{number}} y vuelva a enviar el código.',
  error_sms_opted_out_no_number:
    'Su número se dio de baja de nuestros mensajes. Envíe START al número del que llegan nuestros códigos y vuelva a enviar el código.',
  error_sms_opted_out_by_company:
    'Su número está en la lista de exclusión de mensajes de esta empresa, así que no podemos enviarle un código por mensaje de texto. Pida a la empresa que restablezca los mensajes o elija el correo electrónico.',
  error_send_too_soon: 'Espere {{seconds}} segundos antes de pedir otro código.',
  error_send_failed: 'No se pudo enviar el código. Revise su conexión e inténtelo de nuevo.',
  error_method_not_enrolled:
    'Ese método de verificación no está configurado para su cuenta. Elija otro método o pida a un administrador que lo configure.',
  error_challenge_invalid: 'Su sesión de verificación ya no es válida. Vuelva a iniciar sesión.',
  error_challenge_expired: 'Su sesión de verificación caducó. Vuelva a iniciar sesión.',
  error_no_usable_method:
    'Ninguno de los segundos factores que configuró se puede usar para iniciar sesión aquí. Inicie sesión donde lo configuró o pida a su administrador que restablezca su segundo factor.',
  notice_sms_in_doubt:
    'Es posible que su código ya esté en camino. Revise sus mensajes antes de pedir otro.',
  action_refund: 'Emitir un reembolso',
  action_price_override: 'Anular el precio de una línea',
  action_discount_apply: 'Aplicar un descuento manual',
  action_void_transaction: 'Anular una transacción',
  action_cash_drawer_open: 'Abrir el cajón de efectivo fuera de una venta',
  action_delete_customer: 'Eliminar un cliente',
  action_export_data: 'Exportar datos',
  action_reveal_mail_name_id_numbers:
    'Mostrar el número de identificación completo de un nombre del buzón',
  action_modify_inventory: 'Aprobar un ajuste de inventario',
  action_system_settings: 'Cambiar la configuración del sistema',
  action_order_payment_update: 'Actualizar el pago de un pedido o emitir un reembolso',
  action_order_cancel: 'Cancelar un pedido',
  action_platform_order_refund: 'Reembolsar el pedido de una empresa',
  action_platform_order_cancel: 'Cancelar el pedido de una empresa',
  action_platform_tenant_owner_restore: 'Restablecer al Propietario bloqueado de una empresa',
  action_platform_passkey_register: 'Registrar una clave de acceso',
  action_platform_passkey_remove: 'Quitar una clave de acceso',
  action_platform_stir_shaken_cert_rotate: 'Rotar el certificado STIR/SHAKEN de una empresa',
  action_platform_port_in_cancel: 'Cancelar la portabilidad entrante de una empresa',
  action_platform_connect_account_unlink: 'Desvincular la cuenta de Stripe de una empresa',
  action_price_list_edit: 'Editar la lista de precios de un producto',
  action_coupon_edit: 'Crear, cambiar o eliminar un cupón',
  action_partner_pricing_tier_set: 'Cambiar el nivel de precios de un socio',
  action_partner_api_key_edit: 'Emitir o revocar una clave de API de socio',
  action_partner_delete: 'Eliminar un socio',
  action_partner_status_change: 'Aprobar, rechazar, suspender o reactivar un socio',
  action_stock_count_set: 'Fijar el recuento de existencias de un producto',
  action_mail_address_change:
    'Añadir una cuenta de correo o un reenvío, o cambiar la contraseña de una cuenta de correo',
  action_payroll_submit: 'Enviar una nómina',
  action_payroll_cancel: 'Cancelar una nómina',
  action_payroll_reset: 'Restablecer una nómina',
  action_payroll_skip: 'Omitir un período de pago',
  action_payroll_cancel_submission: 'Cancelar el envío de una nómina',
  action_payroll_approve: 'Aprobar una nómina',
  action_payroll_adjustment_edit: 'Crear o anular un ajuste de nómina',
  action_contractor_payment_create: 'Pagar a un contratista',
  action_garnishment_edit: 'Crear, cambiar o liberar un embargo salarial',
  action_gl_export_edit: 'Crear o anular una exportación al libro mayor',
  action_payroll_connection_edit: 'Conectar, actualizar o desconectar el proveedor de nómina',
  action_password_change: 'Cambiar su contraseña',
  action_mfa_disable: 'Desactivar la autenticación de dos factores',
  action_app_charge_accept: 'Aceptar un cargo recurrente de una aplicación',
  action_mfa_policy_update: 'Cambiar la política de verificación en dos pasos',
  action_field_permissions_edit: 'Cambiar los permisos de campo de un rol',
  action_approval_chain_edit: 'Cambiar la cadena de aprobación o delegar una aprobación',
  action_settings_edit: 'Cambiar la configuración de nómina',
  action_tax_setup_edit: 'Cambiar la configuración fiscal',
  action_company_form_sign: 'Firmar formularios de nómina en nombre del firmante autorizado',
  action_payday_bank_account_link: 'Vincular, agregar o verificar la cuenta bancaria que financia la nómina',
  action_schedule_publish: 'Publicar un horario',
  action_approval_decide: 'Decidir una solicitud de aprobación',
  action_approval_delegate: 'Delegar una aprobación o revocar una delegación',
  action_location_access_edit: 'Cambiar quién puede trabajar en qué ubicaciones',
  action_compliance_jurisdiction_attest: 'Certificar las jurisdicciones de cumplimiento',
  action_overtime_rules_edit: 'Cambiar las reglas de horas extra',
  action_planning_settings_edit: 'Cambiar la configuración de planificación',
  action_ewa_withdraw: 'Solicitar un retiro de salario devengado',
  action_emergency_broadcast_send: 'Enviar una alerta de emergencia',
  action_engagement_settings_edit: 'Cambiar la configuración de participación',
  action_sentiment_collection_toggle: 'Activar o desactivar la recopilación de opiniones',
  action_marketplace_purchase: 'Comprar contenido del mercado',
  action_marketplace_publish: 'Publicar o retirar contenido del mercado',
  action_course_publish: 'Publicar un curso',
  action_short_publish: 'Publicar un video corto de capacitación',
  action_generated_content_publish: 'Publicar contenido generado',
  action_certification_award: 'Otorgar, aprobar o renovar una certificación',
  action_certification_revoke: 'Revocar una certificación',
  action_content_delete:
    'Eliminar un curso, programa, ruta de aprendizaje, evaluación o definición de certificación',
  action_tenant_settings_edit: 'Cambiar la configuración de capacitación',
  action_connector_credentials_set: 'Configurar las credenciales de un conector',
  action_contact_delete: 'Eliminar un contacto',
  action_site_delete: 'Eliminar un sitio',
  action_outreach_settings_edit: 'Cambiar la configuración de contacto',
  action_recording_export: 'Descargar o reenviar una grabación de llamada',
  action_recording_delete: 'Eliminar una grabación de llamada',
  action_voicemail_delete: 'Eliminar un mensaje de voz',
  action_fax_delete: 'Eliminar un fax',
  action_legal_hold_edit: 'Aplicar o levantar una retención legal',
  action_retention_force_run: 'Ejecutar una purga de retención ahora',
  action_ivr_publish: 'Publicar un árbol de IVR',
  action_number_porting:
    'Iniciar, reenviar, cancelar, aprobar o rechazar una portabilidad de número, o gestionar su PIN o su desvío temporal',
  action_number_purchase: 'Comprar un número de teléfono',
  action_number_release: 'Liberar un número de teléfono',
  action_number_departure_confirm:
    'Confirmar que un número de teléfono dejó su cuenta del operador',
  action_stir_shaken_cert_rotate: 'Rotar un certificado STIR/SHAKEN',
  action_stripe_connect_edit:
    'Crear la cuenta de Stripe Connect de la empresa o su enlace de incorporación',
  action_dnc_delete: 'Eliminar una entrada de no llamar',
  action_dnc_export: 'Exportar la lista de no llamar o atender una solicitud GDPR',
  action_compliance_evidence_export: 'Exportar evidencia de cumplimiento',
  action_mailbox_holder_delete: 'Eliminar una suscripción de titular de buzón',
  action_mailbox_holder_pin_reset: 'Restablecer el PIN de un titular de buzón',
  action_mailbox_holder_billing_edit: 'Cambiar o cancelar la facturación de un titular de buzón',
  action_cloned_voice_delete: 'Eliminar una voz clonada',
  action_ai_skill_test_invoke: 'Ejecutar una habilidad de IA con datos reales como prueba',
  action_api_key_edit: 'Crear o revocar una clave de API',
  action_webhook_edit: 'Crear, cambiar o eliminar un webhook',
  action_alert_destination_edit: 'Crear, cambiar o eliminar un destino de alertas',
  action_scim_token_edit: 'Crear o revocar un token SCIM',
  action_carrier_credentials_set: 'Configurar las credenciales de una cuenta de transportista',
  action_carrier_account_delete: 'Eliminar una cuenta de transportista',
  action_sip_device_edit: 'Registrar, cambiar, reaprovisionar o dar de baja un dispositivo SIP',
  action_sip_firmware_stage: 'Preparar un firmware nuevo en un dispositivo SIP',
  action_sso_connection_edit: 'Crear, cambiar o eliminar una conexión SSO',
  action_outbound_campaign_launch: 'Lanzar, cambiar o reanudar una campaña saliente',
  action_outbound_campaign_delete: 'Eliminar una campaña o plantilla saliente',
  action_outbound_template_approve: 'Aprobar una plantilla saliente',
  action_emergency_broadcast_retry: 'Reenviar una alerta de emergencia',
  action_retention_settings_edit: 'Cambiar la retención de grabaciones de llamadas',
  action_consent_settings_edit:
    'Crear, cambiar o eliminar un formulario de captura de consentimiento',
  action_emergency_keywords_edit: 'Cambiar las palabras clave de emergencia',
  action_claim_submit: 'Presentar una reclamación al transportista',
  action_claim_status_set: 'Registrar el resultado de una reclamación',
  action_claim_edit: 'Cambiar los importes de una reclamación',
  action_crawler_agent_key_edit: 'Emitir o revocar una clave de agente rastreador',
  action_claim_delete: 'Eliminar una reclamación o una línea de reclamación',
  action_invoice_delete: 'Eliminar una factura o una línea de factura',
  action_quote_delete: 'Eliminar una cotización',
  action_discrepancy_delete: 'Eliminar una discrepancia',
  actionClass_money: 'Dinero',
  actionClass_credentials: 'Credenciales y secretos',
  actionClass_destruction: 'Destrucción y exportación',
  actionClass_publication: 'Publicación',
  actionClass_authorization: 'Autorización',
  actionClass_compliance_settings: 'Configuración de cumplimiento',
  // PERMISSIONS-CANON-001 S11 (a) / E3: <MfaPolicyBody>.
  policy_title: 'Política de verificación en dos pasos',
  policy_intro:
    'Elija qué acciones piden la verificación en dos pasos. Las exportaciones, las credenciales y las acciones con dinero la piden por defecto: desmarque una para desactivarla en su empresa. Cada cambio queda registrado.',
  policy_read_only: 'Puede ver esta política, pero su rol no puede cambiarla.',
  policy_loading: 'Cargando la política de verificación en dos pasos…',
  policy_error_load: 'No se pudo cargar la política de verificación en dos pasos.',
  policy_error_load_forbidden: 'Su rol no puede ver la política de verificación en dos pasos.',
  policy_retry: 'Intentar de nuevo',
  policy_action_default_on: '(activada por defecto)',
  policy_action_required: '(siempre obligatoria)',
  policy_grace_label: 'Período de gracia (días)',
  policy_grace_hint:
    'Días después de la fecha de inicio antes de exigir la verificación en dos pasos, de 0 a 365.',
  policy_enforce_after_label: 'El requisito comienza (UTC)',
  policy_enforce_after_hint:
    'La fecha, en UTC, a partir de la cual se exige la verificación en dos pasos.',
  policy_save: 'Guardar política',
  policy_saving: 'Guardando…',
  policy_saved: 'La política se guardó.',
  policy_error_save: 'No se pudo guardar la política. Inténtelo de nuevo.',
  policy_error_save_forbidden:
    'Su rol no puede cambiar la política de verificación en dos pasos. Pídaselo a alguien que pueda administrarla.',
  policy_error_save_invalid:
    'La política no fue aceptada. Revise las acciones y las fechas y vuelva a guardar.',
  policy_stepUpOperation: 'cambiar la política de verificación en dos pasos',
  policy_error_save_step_up_cancelled:
    'Confirme su identidad para cambiar la política de verificación en dos pasos.',
  policy_error_grace_range: 'Indique un período de gracia de 0 a 365 días.',
  policy_role_required_label: 'Requiere verificación en dos pasos',
  policy_role_required_hint:
    'Las personas con este rol deben configurar la verificación en dos pasos. El cambio se guarda en la política de la empresa y queda registrado.',
  policy_role_passkey_required_label: 'Requiere una clave de acceso',
  policy_role_passkey_required_hint:
    'Las personas con este rol deben configurar una clave de acceso. El cambio se guarda en la política de la empresa y queda registrado.',
  policy_role_pin_required_label: 'Requiere un PIN',
  policy_role_pin_required_hint:
    'Las personas con este rol deben configurar un PIN para iniciar sesión en una caja. El cambio se guarda en la política de la empresa y queda registrado.',
});

export default MFA_STRINGS_ES;
